import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Filter, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Download,
  RefreshCw,
  AlertCircle,
  Menu
} from 'lucide-react';
import { CSVLink } from "react-csv";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

const IndentApprovalTimeline = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    indentID: '',
    currentLevel: '',
    approvedBy: '',
    status: ''
  });

  const [uniqueValues, setUniqueValues] = useState({
    currentLevels: [],
    approvedBys: [],
    statuses: []
  });

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/ind_approv/indent-approval`);
        const sortedData = response.data.sort((a, b) => new Date(b.Time) - new Date(a.Time));
        setData(sortedData);
        setFilteredData(sortedData);

        setUniqueValues({
          currentLevels: [...new Set(sortedData.map(item => item.CurrentLevel))],
          approvedBys: [...new Set(sortedData.map(item => item.ApprovedBy).filter(Boolean))],
          statuses: [...new Set(sortedData.map(item => item.Status))]
        });

        setLoading(false);
      } catch (err) {
        setError("Failed to fetch indent approvals.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredAndPaginatedData = useMemo(() => {
    let result = [...data];

    if (filters.startDate) {
      const startDateTime = new Date(filters.startDate);
      startDateTime.setHours(0, 0, 0, 0);
      result = result.filter(item => new Date(item.Time) >= startDateTime);
    }

    if (filters.endDate) {
      const endDateTime = new Date(filters.endDate);
      endDateTime.setHours(23, 59, 59, 999);
      result = result.filter(item => new Date(item.Time) <= endDateTime);
    }

    if (filters.indentID) {
      result = result.filter(item => 
        item.IndentID.toString().includes(filters.indentID)
      );
    }
    if (filters.currentLevel) {
      result = result.filter(item => item.CurrentLevel === filters.currentLevel);
    }
    if (filters.approvedBy) {
      result = result.filter(item => item.ApprovedBy === filters.approvedBy);
    }
    if (filters.status) {
      result = result.filter(item => item.Status === filters.status);
    }

    setFilteredData(result);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return result.slice(indexOfFirstItem, indexOfLastItem);
  }, [filters, data, currentPage, itemsPerPage]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'Approved': return 'bg-green-100 text-green-800';
      case 'Rejected': return 'bg-red-100 text-red-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      indentID: '',
      currentLevel: '',
      approvedBy: '',
      status: ''
    });
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const csvData = filteredData.map(item => ({
    'Indent ID': item.IndentID,
    'Project Name': item.ProjectName,
    'Current Level': item.CurrentLevel,
    'Status': item.Status,
    'Approved By': item.ApprovedBy || 'N/A',
    'Time': new Date(item.Time).toLocaleString()
  }));

  const FilterModal = () => (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="bg-gray-100 p-6 rounded-t-xl border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">Filter Change Requests</h2>
            <button 
              onClick={() => setShowFilterModal(false)}
              className="text-gray-500 hover:text-gray-700 transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
              <input 
                type="date" 
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <input 
                type="date" 
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Level</label>
              <select 
                value={filters.currentLevel}
                onChange={(e) => setFilters({...filters, currentLevel: e.target.value})}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Levels</option>
                {uniqueValues.currentLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select 
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                {uniqueValues.statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Search by Indent ID</label>
            <input 
              type="text" 
              value={filters.indentID}
              onChange={(e) => setFilters({...filters, indentID: e.target.value})}
              placeholder="Enter Indent ID"
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
            <button 
              onClick={clearFilters} 
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition"
            >
              Clear Filters
            </button>
            <button 
              onClick={() => setShowFilterModal(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const MobileCard = ({ item }) => (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="font-bold text-gray-800">#{item.IndentID}</span>
        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(item.Status)}`}>
          {item.Status}
        </span>
      </div>
      <div className="text-sm text-gray-600 mb-1">
        <strong>Project:</strong> {item.ProjectName}
      </div>
      <div className="text-sm text-gray-600 mb-1">
        <strong>Current Level:</strong> 
        <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full ml-2">
          {item.CurrentLevel}
        </span>
      </div>
      <div className="text-sm text-gray-600 mb-1">
        <strong>Approved By:</strong> {item.ApprovedBy || 'Pending'}
      </div>
      <div className="text-xs text-gray-500 text-right">
        {new Date(item.Time).toLocaleString()}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="bg-white shadow-lg rounded-xl border">
          <div className="bg-gray-100 px-4 py-4 border-b flex justify-between items-center">
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-gray-800 flex items-center">
                <RefreshCw className="mr-2 sm:mr-3 text-blue-600 w-4 h-4 sm:w-6 sm:h-6" /> 
                Change Request Timeline
              </h1>
              <p className="text-gray-500 text-xs sm:text-sm mt-1">
                Track and manage your indent approval process
              </p>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => setShowFilterModal(true)} 
                className="flex items-center bg-blue-50 text-blue-700 px-2 py-1 sm:px-4 sm:py-2 rounded-md hover:bg-blue-100 transition"
              >
                <Filter className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> 
                <span className="hidden sm:inline">Filter</span>
              </button>
              <CSVLink 
                data={csvData} 
                filename="indent_approvals.csv"
                className="flex items-center bg-green-50 text-green-700 px-2 py-1 sm:px-4 sm:py-2 rounded-md hover:bg-green-100 transition"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> 
                <span className="hidden sm:inline">Export</span>
              </CSVLink>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <RefreshCw className="animate-spin text-blue-600 w-8 h-8" />
              </div>
            ) : error ? (
              <div className="flex justify-center items-center h-64 text-red-500">
                <AlertCircle className="mr-2" /> {error}
              </div>
            ) : (
              <div>
                {isMobileView ? (
                  <div>
                    {filteredAndPaginatedData.map(item => (
                      <MobileCard key={item.IndentID} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100 border-b">
                          <th className="py-3 px-4 text-left text-gray-600 font-semibold">Indent ID</th>
                          <th className="py-3 px-4 text-left text-gray-600 font-semibold">Project Name</th>
                          <th className="py-3 px-4 text-left text-gray-600 font-semibold">Current Level</th>
                          <th className="py-3 px-4 text-left text-gray-600 font-semibold">Status</th>
                          <th className="py-3 px-4 text-left text-gray-600 font-semibold">Approved By</th>
                          <th className="py-3 px-4 text-left text-gray-600 font-semibold">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndPaginatedData.map(item => (
                          <tr key={item.IndentID} className="border-b hover:bg-gray-50 transition">
                            <td className="py-3 px-4 font-medium text-gray-800">
                              {item.IndentID}
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              {item.ProjectName}
                            </td>
                            <td className="py-3 px-4">
                              <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                                {item.CurrentLevel}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(item.Status)}`}>
                                {item.Status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              {item.ApprovedBy || 'Pending'}
                            </td>
                            <td className="py-3 px-4 text-gray-500 text-sm">
                              {new Date(item.Time).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {filteredData.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No change requests found matching your filters.
                  </div>
                )}

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 border-t pt-4">
                  <div className="text-gray-600 text-sm mb-2 sm:mb-0">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                    {Math.min(currentPage * itemsPerPage, filteredData.length)} of{' '}
                    {filteredData.length} entries
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => paginate(currentPage - 1)} 
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 sm:px-4 sm:py-2 border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button 
                      onClick={() => paginate(currentPage + 1)} 
                      disabled={currentPage * itemsPerPage >= filteredData.length}
                      className="px-3 py-1.5 sm:px-4 sm:py-2 border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {showFilterModal && <FilterModal />}
      <Footer />
    </div>
  );
};

export default IndentApprovalTimeline;